const TOKEN_KEY = 'apl.token';
const REFRESH_KEY = 'apl.refresh';

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  set: (access: string, refresh?: string) => {
    localStorage.setItem(TOKEN_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    ticket = null;
  },
};

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: { feld: string; meldung: string }[]
  ) {
    super(message);
  }
}

let refreshing: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  const refreshToken = tokenStore.getRefresh();
  if (!refreshToken) return false;
  refreshing ??= (async () => {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      tokenStore.set(data.accessToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit & { raw?: boolean } = {}
): Promise<T> {
  const doFetch = async () => {
    const headers = new Headers(init.headers);
    const token = tokenStore.get();
    if (token) headers.set('Authorization', `Bearer ${token}`);
    if (init.body && !(init.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }
    return fetch(`/api${path}`, { ...init, headers });
  };

  let res = await doFetch();

  // Abgelaufenes Access-Token einmalig erneuern und Anfrage wiederholen.
  if (res.status === 401 && tokenStore.getRefresh()) {
    if (await tryRefresh()) res = await doFetch();
  }

  if (!res.ok) {
    let msg = `Fehler ${res.status}`;
    let details: { feld: string; meldung: string }[] | undefined;
    try {
      const body = await res.json();
      msg = body.fehler ?? msg;
      details = body.details;
    } catch {
      /* keine JSON-Antwort */
    }
    if (res.status === 401) tokenStore.clear();
    throw new ApiError(res.status, msg, details);
  }

  if (init.raw) return res as unknown as T;
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/**
 * Zugriffsticket fuer Bild-URLs.
 * <img> kann keinen Authorization-Header senden - daher ein kurzlebiges,
 * auf Dateizugriff beschraenktes Ticket, das im Speicher gehalten wird.
 */
let ticket: { value: string; expiresAt: number } | null = null;
let ticketPromise: Promise<string> | null = null;

export async function assetTicket(): Promise<string> {
  if (ticket && ticket.expiresAt > Date.now() + 30_000) return ticket.value;
  ticketPromise ??= api<{ ticket: string; gueltigSekunden: number }>('/assets/ticket')
    .then((r) => {
      ticket = { value: r.ticket, expiresAt: Date.now() + r.gueltigSekunden * 1000 };
      return r.ticket;
    })
    .finally(() => {
      ticketPromise = null;
    });
  return ticketPromise;
}

export const clearAssetTicket = () => {
  ticket = null;
};

/** Datei-URL inkl. Zugriffsticket. */
export const fileUrl = (relPath: string, t?: string) => {
  const path = relPath.split(/[\\/]/).map(encodeURIComponent).join('/');
  return `/api/assets/dateien/${path}${t ? `?t=${encodeURIComponent(t)}` : ''}`;
};

export const schematicUrl = (file: string) => `/api/assets/schematics/${file}`;
